import React, { useRef } from 'react';
import { FaUndo, FaCopy, FaCheck, FaTimes } from 'react-icons/fa';
import {
  ASPECT_PRESETS,
  MIN_SIZE,
  clamp01,
  moveRect,
  rectFromPoints,
  resizeRect,
  rectToCrop,
} from './cropUtils';
import './crop-studio.css';

const CORNER_HANDLES = ['nw', 'ne', 'sw', 'se'];
const EDGE_HANDLES = ['n', 's', 'w', 'e'];
const pct = (v) => `${(v * 100).toFixed(4)}%`;

/**
 * Crop layer drawn exactly over the visible video picture (not the letterbox).
 * The player underneath keeps playing; this layer only captures pointer input.
 */
export function CropOverlay({
  frame,
  rect,
  onChange,
  normRatio = null,
  sourceSize,
  interactive = true,
  closing = false,
}) {
  const rootRef = useRef(null);
  const dragRef = useRef(null);

  const toNorm = (e) => {
    const b = rootRef.current.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - b.left) / (b.width || 1)),
      y: clamp01((e.clientY - b.top) / (b.height || 1)),
    };
  };

  const handlePointerDown = (e) => {
    if (!interactive || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const p = toNorm(e);
    const handle = e.target.closest('[data-crop-handle]')?.dataset.cropHandle;
    const onBox = !!e.target.closest('.crop-box');

    let drag;
    if (handle && rect) drag = { type: 'resize', handle, origin: rect };
    else if (onBox && rect) drag = { type: 'move', start: p, origin: rect };
    else drag = { type: 'draw', anchor: p, origin: rect };

    dragRef.current = { ...drag, pointerId: e.pointerId, moved: false, last: rect };
    try { rootRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const p = toNorm(e);
    let next;
    if (d.type === 'move') next = moveRect(d.origin, p.x - d.start.x, p.y - d.start.y);
    else if (d.type === 'resize') next = resizeRect(d.origin, d.handle, p, normRatio);
    else next = rectFromPoints(d.anchor, p, normRatio);
    d.moved = true;
    d.last = next;
    onChange(next);
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try { rootRef.current?.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.type === 'draw') {
      const r = d.last;
      const tooSmall = !r || r.w < MIN_SIZE || r.h < MIN_SIZE;
      if (d.moved && tooSmall) onChange(d.origin);
    }
  };

  const handleBoxKeyDown = (e) => {
    if (!rect) return;
    const step = e.shiftKey ? 0.05 : 0.01;
    const delta = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }[e.key];
    if (!delta) return;
    e.preventDefault();
    onChange(moveRect(rect, delta[0], delta[1]));
  };

  if (!frame) return null;

  const payload = rect ? rectToCrop(rect, sourceSize) : null;
  const handles = normRatio ? CORNER_HANDLES : [...CORNER_HANDLES, ...EDGE_HANDLES];

  return (
    <div
      ref={rootRef}
      className={[
        'crop-overlay',
        closing ? 'is-closing' : '',
        rect ? '' : 'is-empty',
      ].filter(Boolean).join(' ')}
      style={{
        '--crop-frame-left': `${frame.left}px`,
        '--crop-frame-top': `${frame.top}px`,
        '--crop-frame-width': `${frame.width}px`,
        '--crop-frame-height': `${frame.height}px`,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {!rect && (
        <div className="crop-overlay__hint">Drag on the video to select an area</div>
      )}

      {rect && (
        <div
          className="crop-box"
          style={{ left: pct(rect.x), top: pct(rect.y), width: pct(rect.w), height: pct(rect.h) }}
          tabIndex={interactive ? 0 : -1}
          role="group"
          aria-label="Crop area. Drag to move, use arrow keys to nudge."
          onKeyDown={handleBoxKeyDown}
        >
          <div className="crop-box__grid" aria-hidden="true" />
          {handles.map((h) => (
            <span
              key={h}
              className={`crop-box__handle crop-box__handle--${h}`}
              data-crop-handle={h}
              aria-hidden="true"
            />
          ))}
          {payload && (
            <span className="crop-box__size">
              {payload.width} × {payload.height}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const shortTime = (t) => {
  if (!t) return '';
  return String(t).replace(/^00:/, '').replace(/\.\d+$/, '');
};

/** Controls shown under the player while cropping. */
export function CropPanel({
  closing = false,
  compact = false,
  aspect,
  onAspectChange,
  onReset,
  clips = [],
  targets = [],
  onTargetsChange,
  payload,
  sourceSize,
  onApply,
  onRemove,
  onCopy,
  onCancel,
}) {
  const targetSet = new Set(targets);
  const toggleTarget = (i) => {
    const next = new Set(targetSet);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    onTargetsChange([...next].sort((a, b) => a - b));
  };
  const selectAll = () => onTargetsChange(clips.map((_, i) => i));
  const selectNone = () => onTargetsChange([]);
  const canRemove = targets.some((i) => clips[i]?.crop);
  const canApply = !!payload && targets.length > 0;
  const applyLabel = targets.length === 1
    ? `Apply to clip ${targets[0] + 1}`
    : `Apply to ${targets.length} clips`;

  return (
    <div
      className={[
        'crop-panel',
        closing ? 'is-closing' : '',
        compact ? 'crop-panel--compact' : '',
      ].filter(Boolean).join(' ')}
      role="region"
      aria-label="Crop controls"
    >
      <div className="crop-panel__row">
        <span className="crop-panel__label">Shape</span>
        <div className="crop-panel__aspects" role="radiogroup" aria-label="Aspect ratio">
          {ASPECT_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={aspect === p.id}
              className={`crop-chip ${aspect === p.id ? 'is-active' : ''}`}
              onClick={() => onAspectChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="crop-panel__section">
        <div className="crop-panel__section-head">
          <span className="crop-panel__label">Apply to</span>
          {clips.length > 1 && (
            <span className="crop-panel__quick">
              <button type="button" onClick={selectAll}>All</button>
              <button type="button" onClick={selectNone}>None</button>
            </span>
          )}
        </div>
        {clips.length === 0 ? (
          <p className="crop-panel__empty">
            No clips yet. Mark a clip first, then apply this crop to it.
          </p>
        ) : (
          <div className="crop-panel__clips">
            {clips.map((clip, i) => {
              const selected = targetSet.has(i);
              return (
                <button
                  key={i}
                  type="button"
                  className={[
                    'crop-clip-chip',
                    selected ? 'is-selected' : '',
                    clip.crop ? 'has-crop' : '',
                  ].filter(Boolean).join(' ')}
                  aria-pressed={selected}
                  onClick={() => toggleTarget(i)}
                  title={clip.crop ? `Clip ${i + 1} already has a ${clip.crop.width}×${clip.crop.height} crop` : `Clip ${i + 1}`}
                >
                  <span className="crop-clip-chip__check" aria-hidden="true">
                    {selected && <FaCheck />}
                  </span>
                  <span className="crop-clip-chip__name">Clip {i + 1}</span>
                  {clip.startTime && (
                    <span className="crop-clip-chip__time">{shortTime(clip.startTime)}</span>
                  )}
                  {clip.crop && <span className="crop-clip-chip__dot" aria-label="already cropped" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="crop-panel__footer">
        <div className="crop-panel__info">
          {payload ? (
            <>
              <strong>{payload.width} × {payload.height}</strong>
              <span>at {payload.x}, {payload.y} · source {sourceSize.width}×{sourceSize.height}</span>
            </>
          ) : (
            <span>Drag on the video to draw a crop area.</span>
          )}
        </div>

        <div className="crop-panel__actions">
          <button type="button" className="crop-btn crop-btn--ghost crop-btn--icon" onClick={onCopy} disabled={!payload} title="Copy crop JSON">
            <FaCopy aria-hidden="true" />
          </button>
          <button type="button" className="crop-btn crop-btn--ghost" onClick={onReset} title="Reset crop area">
            <FaUndo aria-hidden="true" />
            <span>Reset</span>
          </button>
          {canRemove && (
            <button type="button" className="crop-btn crop-btn--danger" onClick={onRemove} title="Remove crop from selected clips">
              <FaTimes aria-hidden="true" />
              <span>Remove</span>
            </button>
          )}
          <button type="button" className="crop-btn crop-btn--secondary" onClick={onCancel} title="Cancel (Esc)">
            Cancel
          </button>
          <button
            type="button"
            className="crop-btn crop-btn--primary"
            onClick={onApply}
            disabled={!canApply}
            title={canApply ? 'Apply (Enter)' : 'Select at least one clip'}
          >
            <FaCheck aria-hidden="true" />
            <span>{targets.length ? applyLabel : 'Select a clip'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
